import { useState, useEffect, useMemo } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { Card, SubHeader, Msg } from '../../components/Leagues/ui'
import Ionicons from '@expo/vector-icons/Ionicons'
import GradientButton from '../../components/GradientButton'
import Sheet from './Sheet'

export default function SezioneCalendario({ leagueId, isAdmin }) {
  const { competitionId } = useAuth()   // solo le gare della competizione attiva (uomini/donne)
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [open, setOpen]               = useState(false)
  const [races, setRaces]             = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [loading, setLoading]         = useState(false)
  const [saving, setSaving]           = useState(false)
  const [msg, setMsg]                 = useState(null)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    Promise.all([
      supabase.from('dim_race').select('id_race, name, race_date, id_type_race').eq('is_deleted', false).eq('id_competition', competitionId).order('race_date'),
      supabase.from('fact_league_calendar').select('id_race').eq('id_league', leagueId),
    ]).then(([allRes, calRes]) => {
      setRaces(allRes.data ?? [])
      setSelectedIds(calRes.data?.map(c => c.id_race) ?? [])
      setLoading(false)
    })
  }, [open, leagueId])

  const toggle = (id) => {
    if (!isAdmin) return
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleSave = async () => {
    setSaving(true); setMsg(null)
    try {
      await supabase.from('fact_league_calendar').delete().eq('id_league', leagueId)
      if (selectedIds.length > 0) await supabase.from('fact_league_calendar').insert(selectedIds.map(id => ({ id_league: leagueId, id_race: id })))
      setMsg({ type: 'success', text: t('league.calendar_saved') })
    } catch (e) { setMsg({ type: 'error', text: e.message }) } finally { setSaving(false) }
  }

  return (
    <>
      <Card>
        <View style={styles.cardRow}>
          <View style={{ flex: 1 }}>
            <SubHeader icon={<Ionicons name="calendar-outline" size={16} color={tc.rosaText} />}>{t('league.calendar_section')}</SubHeader>
            <Text style={[styles.cardDesc, { color: tc.textMuted }]}>{isAdmin ? t('league.calendar_desc_admin') : t('league.calendar_desc_user')}</Text>
          </View>
          <GradientButton onPress={() => setOpen(true)} size="sm">{isAdmin ? t('league.manage') : t('league.view')}</GradientButton>
        </View>
      </Card>
      <Sheet visible={open} title={t('league.calendar_title')} onClose={() => setOpen(false)}
        footer={isAdmin ? <GradientButton onPress={handleSave} loading={saving} style={{ width: '100%' }}>{t('league.calendar_save', { count: selectedIds.length })}</GradientButton> : null}
      >
        {msg && <Msg type={msg.type} text={msg.text} />}
        {isAdmin && (
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <Text style={{ fontSize: 12, color: tc.textMuted }}>{t('league.races_selected', { count: selectedIds.length })}</Text>
            <TouchableOpacity onPress={() => setSelectedIds(selectedIds.length === races.length ? [] : races.map(r => r.id_race))} accessibilityRole="button" style={{ minHeight: 44, justifyContent: 'center', paddingLeft: 12 }}>
              <Text style={{ fontSize: 12, fontWeight: '700', color: tc.rosaText }}>{selectedIds.length === races.length ? t('league.deselect_all') : t('league.select_all')}</Text>
            </TouchableOpacity>
          </View>
        )}
        {loading ? <Text style={{ color: tc.textMuted, fontSize: 13, textAlign: 'center', padding: 20 }}>{t('league.loading')}</Text> : (
          <View style={{ gap: 8 }}>
            {races.map(r => {
              const checked = selectedIds.includes(r.id_race)
              return (
                <TouchableOpacity key={r.id_race} onPress={() => toggle(r.id_race)} style={[styles.calRaceRow, { backgroundColor: checked ? tc.rosaSubtle : tc.bgCard2, borderColor: checked ? tc.rosaBorder : tc.border }]} activeOpacity={isAdmin ? 0.7 : 1} accessibilityRole={isAdmin ? 'checkbox' : undefined} accessibilityState={{ checked, disabled: !isAdmin }} accessibilityLabel={`${r.name}, ${r.race_date}`}>
                  {isAdmin && (
                    <View style={[styles.calCheckbox, { borderColor: checked ? tc.rosa : tc.border, backgroundColor: checked ? tc.rosa : 'transparent' }]}>
                      {checked && <Ionicons name="checkmark" size={14} color={tc.brandText} />}
                    </View>
                  )}
                  {!isAdmin && checked && <Ionicons name="checkmark" size={16} color={tc.rosaText} />}
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.calRaceName, { color: tc.text }]}>{r.name}</Text>
                    <Text style={[styles.calRaceDate, { color: tc.textMuted }]}>{r.race_date}</Text>
                  </View>
                </TouchableOpacity>
              )
            })}
          </View>
        )}
      </Sheet>
    </>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  cardRow:     { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  cardDesc:    { fontSize: 12, marginTop: -8 },
  calRaceRow:  { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, paddingHorizontal: 14, borderRadius: 12, borderWidth: 1 },
  calCheckbox: { width: 20, height: 20, borderRadius: 6, borderWidth: 1.5, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  calRaceName: { fontSize: 14, fontWeight: '600' },
  calRaceDate: { fontSize: 12, marginTop: 2 },
})